import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import { useGlobalContext } from '../GlobalContextProvider';
import axios from 'axios';
import { useAccountIdContext } from '../components/account/AccountIdContext';
import { useCallback, useMemo } from 'react';
import { msalConfig } from '../authConfig';

export const tmsBaseUrl = '/api/proxy/tms';
const disableAxiosHttpClientFeatureFlag = false;

function useDefaultHeaders(useAuthentication: boolean = true, pillarCode: string = undefined) {
  const { tenantId, getAuthTokenForPillar } = useGlobalContext();
  const accountContext = useAccountIdContext();
  const authToken = useAuthentication && msalConfig.enabled ? getAuthTokenForPillar(pillarCode) : undefined;
  return useMemo(() => {
    const disabledTimeTravelTenantIds = process.env.DISABLED_TIME_TRAVEL_TENANT_IDS.split(',');

    if (disabledTimeTravelTenantIds.includes(tenantId)) {
      return {
        'context-tenantid': tenantId,
        'x-stratus-auth': authToken,
        'x-stratusswa-auth': 'AzBjl1VJg9b0iOMrdsP4',
      };
    }

    const billingDate = accountContext?.billingDate || new Date();
    const timeOffSet = `${differenceInCalendarDays(billingDate, new Date())}:0:00:00`;
    return {
      'context-datetimeoffset': timeOffSet,
      'context-tenantid': tenantId,
      'x-stratus-auth': authToken,
      'x-stratusswa-auth': 'AzBjl1VJg9b0iOMrdsP4', // Placeholder for Swa auth header
    };
  }, [accountContext?.billingDate, tenantId, authToken]);
}

export function useGetWithNoStratusContext(useAuthentication: boolean = true, pillarCode: string = undefined) {
  const { getAuthTokenForPillar } = useGlobalContext();
  const authToken = useAuthentication ? getAuthTokenForPillar(pillarCode) : undefined;
  return async <T>(input: string, headers = {}) => {
    if (disableAxiosHttpClientFeatureFlag) {
      const response = await fetch(input, { method: 'GET', headers: { ...headers, 'x-stratus-auth': authToken } });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return (await response.json()) as T;
    }

    return await axios
      .get<T>(input, { headers: { ...headers, 'x-stratus-auth': authToken } })
      .then((response) => response.data);
  };
}

export function useGet(useAuthentication: boolean = false, pillarCode: string = undefined) {
  const defaultHeaders = useDefaultHeaders(useAuthentication, pillarCode);
  return useCallback(
    async <T>(input: string, headers = {}) => {
      headers = { ...defaultHeaders, ...headers };

      if (disableAxiosHttpClientFeatureFlag) {
        const response = await fetch(input, { method: 'GET', headers });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        return (await response.json()) as T;
      }

      return await axios.get<T>(input, { headers }).then((response) => response.data);
    },
    [defaultHeaders]
  );
}

export function useDelete(useAuthentication: boolean = false, pillarCode: string = undefined) {
  const defaultHeaders = useDefaultHeaders(useAuthentication, pillarCode);
  return useCallback(
    async (input: string, headers = {}) => {
      headers = { ...defaultHeaders, ...headers };

      if (disableAxiosHttpClientFeatureFlag) {
        const response = await fetch(input, { method: 'DELETE', headers });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        return await response.json();
      }

      return await axios.delete(input, { headers }).then((response) => response.data);
    },
    [defaultHeaders]
  );
}

export function usePost(useAuthentication: boolean = false, pillarCode: string = undefined) {
  const defaultHeaders = useDefaultHeaders(useAuthentication, pillarCode);

  return useCallback(
    async <T>(input: string, body: any, headers = {}) => {
      headers = { ...defaultHeaders, ...headers };
      if (disableAxiosHttpClientFeatureFlag) {
        const response = await fetch(input, {
          method: 'POST',
          headers: { 'content-type': 'application/json', ...headers },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        return (await response.json()) as T;
      }

      return await axios
        .post<T>(input, body, {
          headers: { 'content-type': 'application/json', ...headers },
        })
        .then((response) => response.data);
    },
    [defaultHeaders]
  );
}

export function usePut(useAuthentication: boolean = false, pillarCode: string = undefined) {
  const defaultHeaders = useDefaultHeaders(useAuthentication, pillarCode);
  return useCallback(
    async <T>(input: string, body: any, headers = {}) => {
      headers = { ...defaultHeaders, ...headers };

      if (disableAxiosHttpClientFeatureFlag) {
        const response = await fetch(input, {
          method: 'PUT',
          headers: { 'content-type': 'application/json', ...headers },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        return (await response.json()) as T;
      }

      return await axios
        .put<T>(input, body, {
          headers: { 'content-type': 'application/json', ...headers },
        })
        .then((response) => response.data);
    },
    [defaultHeaders]
  );
}
